const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

export function containsHtml(value: string | undefined | null): boolean {
  return Boolean(value?.trim() && HTML_TAG_PATTERN.test(value.trim()));
}

const SECTION_HEADER_PATTERN =
  /^(Company Description|Job Description|Qualifications|Basic Qualifications|Preferred Qualifications|Key job responsibilities|Key Job Responsibilities|Responsibilities|Requirements|Expectations(?:\s+[–-]\s+.+)?|Nice to have|Description)$/i;

const INLINE_SECTION_HEADERS = [
  'Basic Qualifications',
  'Preferred Qualifications',
  'Key Job Responsibilities',
  'Key job responsibilities',
  'Company Description',
  'Job Description',
  'Qualifications',
  'Responsibilities',
  'Requirements',
  'Nice To Have',
  'Nice to have',
].sort((left, right) => right.length - left.length);

const LIST_ITEM_PATTERN = /^[-•*·]\s+/;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function formatPlainJobDescription(
  text: string | undefined | null
): string {
  if (!text?.trim()) {
    return '';
  }

  let formatted = text.replace(/\r\n/g, '\n').trim();

  if ((formatted.match(/\n/g) || []).length >= 4) {
    return formatted.replace(/\n{3,}/g, '\n\n').trim();
  }

  for (const header of INLINE_SECTION_HEADERS) {
    const headerPattern = escapeRegExp(header);
    formatted = formatted.replace(
      new RegExp(`\\s+(${headerPattern})\\s+`, 'gi'),
      `\n\n$1\n\n`
    );
    formatted = formatted.replace(
      new RegExp(`^(${headerPattern})\\s+`, 'i'),
      '$1\n\n'
    );
  }

  formatted = formatted.replace(/\s+(Expectations\s+[–-]\s+)/gi, '\n\n$1');

  formatted = formatted.replace(/\s+-\s+(?=[A-Za-z0-9(])/g, '\n- ');
  formatted = formatted.replace(/\s+•\s+/g, '\n• ');

  return formatted.replace(/\n{3,}/g, '\n\n').trim();
}

export function isSectionHeaderLine(line: string): boolean {
  return SECTION_HEADER_PATTERN.test(line.trim());
}

export function isListItemLine(line: string): boolean {
  return LIST_ITEM_PATTERN.test(line.trim());
}

export function normalizeListItem(line: string): string {
  return line.trim().replace(LIST_ITEM_PATTERN, '');
}

export type JobDescriptionBlock =
  | { type: 'section'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] };

export function parseJobDescriptionBlocks(
  text: string | undefined | null
): JobDescriptionBlock[] {
  const formatted = formatPlainJobDescription(text);
  if (!formatted) {
    return [];
  }

  const blocks: JobDescriptionBlock[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) {
      return;
    }

    blocks.push({
      type: 'paragraph',
      text: paragraphLines.join(' ').trim(),
    });
    paragraphLines = [];
  };

  const flushList = () => {
    if (listItems.length === 0) {
      return;
    }

    blocks.push({ type: 'list', items: [...listItems] });
    listItems = [];
  };

  for (const rawLine of formatted.split('\n')) {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      flushParagraph();
      continue;
    }

    if (isSectionHeaderLine(line)) {
      flushList();
      flushParagraph();
      blocks.push({ type: 'section', text: line });
      continue;
    }

    if (isListItemLine(line)) {
      flushParagraph();
      listItems.push(normalizeListItem(line));
      continue;
    }

    flushList();
    paragraphLines.push(line);
  }

  flushList();
  flushParagraph();

  return blocks;
}
